import { create } from 'zustand';
import { kitchenFor } from '@shripos/shared/license';

/**
 * Kiosk session state. Credentials live in httpOnly cookies; this mirrors:
 *  - me:     the signed-in user profile from /auth/me (null when signed out)
 *  - device: the registered device on this browser (null when not registered)
 *  - lock:   a full-screen lock (suspended account) with its reason
 *  - offline: signed in with a PIN checked on this device while offline (no server session for
 *            this person yet: the app signs in for real when the connection is back)
 */
export const useSession = create((set) => ({
  status: 'loading', // loading | authenticated | anonymous | locked
  me: null,
  device: null,
  lock: null,
  offline: false,
  notice: null, // message for the login screen (e.g. "signed out", "device revoked")

  setDevice: (device) => set({ device }),
  signedIn: (me) => set({ status: 'authenticated', me, lock: null, notice: null, offline: false }),
  signedInOffline: (me) => set({ status: 'authenticated', me, lock: null, notice: null, offline: true }),
  setMe: (me) => set({ me }),
  signedOut: (notice = null) => set({ status: 'anonymous', me: null, notice, offline: false }),
  locked: (lock) => set({ status: 'locked', me: null, lock }),
}));

export function can(me, permission) {
  return Boolean(me?.user?.isOwner || me?.user?.permissions?.includes(permission));
}

export function useCan(permission) {
  return useSession((s) => can(s.me, permission));
}

/**
 * The kitchen at the signed-in outlet: { orders, display }. Orders (KOT button, kitchen tickets)
 * need the plan feature "Kitchen orders (KOT)" and the outlet's "Send orders to a kitchen" switch;
 * the display also needs "Kitchen display (KDS)". A session saved by an older version has no
 * `kitchen`: the plan decides.
 */
export function kitchenOf(me) {
  return me?.kitchen ?? kitchenFor(me?.license?.features, null);
}

/** The kitchen the plan allows, whatever the outlet: for business-wide settings (KOT layout, stations). */
export function planKitchen(me) {
  return kitchenFor(me?.license?.features, null);
}
